import React, { useEffect, useRef, useState } from 'react'
import AdminPageTitle from '../../components/AdminPageTitle/AdminPageTitle'
import { Button, Upload, Select, Table, Space, Tag, message } from 'antd'
import { OrderedListOutlined, UploadOutlined } from '@ant-design/icons'
import { Layer } from '../../services/models'
import { Helmet } from 'react-helmet-async'
import layersService from '../../services/layersService'
import { API_URL } from '../../services/apiService'
import tokenService from '../../services/tokenService'
import objImportsService, { ObjImport, GetStepName, GetStepColor } from '../../services/objImportsService'
import { useNavigate } from 'react-router-dom'

type Props = {}

const AdminImportListPage: React.FC<Props> = () => {
  const navigate = useNavigate()

  const [createNew, setCreateNew] = useState<boolean>(false)
  const [objImports, setObjImports] = useState<ObjImport[]>([])
  const [layers, setLayers] = useState<Layer[]>([])
  const [layerNameById, setLayerNameById] = useState<Map<number, string>>(new Map())
  const [layerId, setLayerId] = useState<string>('0')
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const fetchingRef = useRef(false)

  const fetchData = async () => {
    if (fetchingRef.current) return
    fetchingRef.current = true
    setLoading(true)
    try {
      const layersResponse = await layersService.list()
      const newLayerNameById = new Map<number, string>(
        layersResponse.layers.map((l) => [l.id, l.name])
      )
      const importsResponse = await objImportsService.list()

      setLayerNameById(newLayerNameById)
      setLayers([...layersResponse.layers])
      setObjImports(importsResponse.imports)
    } catch (error) {
      console.error('Failed to fetch data:', error)
    } finally {
      fetchingRef.current = false
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
  }, [])

  useEffect(() => {
    const hasActive = objImports.some(i => i.step === 4 || i.step === 5)
    if (!hasActive) return
    const interval = setInterval(() => {
      fetchData()
    }, 5000)
    return () => clearInterval(interval)
  }, [objImports])

  return (
    <div>
      <Helmet><title>Prisma — Импорты</title></Helmet>
      {!createNew && (
        <div>
          <AdminPageTitle title={'Импорты'} buttonText={'Создать'} onClickButton={() => setCreateNew(true)}/>

          <Table loading={loading} dataSource={objImports} columns={[
            {
              title: 'ID',
              dataIndex: 'id',
              key: 'id',
              width: 80
            },
            {
              title: 'Импорт',
              render: (_, i) => (
                <div>
                  <div>{i.filename}</div>
                  <div style={{ fontSize: 12, color: '#888' }}>{i.description || '—'}</div>
                </div>
              )
            },
            {
              title: 'Слой',
              render: (_, i) => layerNameById.get(i.layerId)
            },
            {
              title: 'Шаг',
              dataIndex: 'step',
              render: (step: number) => <Tag color={GetStepColor(step)}>{GetStepName(step)}</Tag>
            },
            {
              title: 'Результат',
              render: (_, i: ObjImport) => {
                if ((i.step === 4 || i.step === 5) && i.result && i.result.total > 0) {
                  return `Обработано ${i.result.processed || 0} / ${i.result.total}`
                }
                if (i.step === 6 && i.result && i.result.create !== undefined) {
                  return `${i.result.create} / ${i.result.update} / ${i.result.error}`
                }
                return '—'
              }
            },
            {
              title: 'Действия',
              key: 'actions',
              render: (_: any, record: any) => (
                <Space size="middle">
                  <a onClick={() => navigate(`/import/${record.id}`)}>Открыть</a>
                </Space>
              )
            }
          ]} rowKey={'id'} pagination={false}/>
        </div>
      )}

      {createNew && (
        <div>
          <AdminPageTitle title={'Новый импорт'} buttonText={'Список'} icon={<OrderedListOutlined/>}
                          onClickButton={() => setCreateNew(false)}/>

          <Space direction="vertical" style={{ width: '100%' }}>
            <Select
              style={{ width: 200 }}
              placeholder={'Выберите слой'}
              options={layers.map(l => ({ label: l.name, value: l.id.toString() }))}
              onChange={(e) => setLayerId(e)}
            />

            <Upload
              action={`${API_URL}/obj-imports/upload`}
              headers={{ authorization: `Bearer ${tokenService.get()}` }}
              name="file"
              multiple={false}
              data={{ layerId: +layerId }}
              maxCount={1}
              showUploadList={false}
              disabled={!layerId || layerId === '0'}
              onChange={(e) => {
                if (e.file.status === 'uploading') {
                  setUploading(true)
                }
                if (e.file.status === 'done') {
                  setUploading(false)
                  message.success('Файл загружен')
                  setCreateNew(false)
                  if (e.file.response.id) {
                    navigate(`/import/${e.file.response.id}`)
                  }
                }
                if (e.file.status === 'error') {
                  setUploading(false)
                  message.error('Ошибка при загрузке файла')
                }
              }}
            >
              <Button
                icon={<UploadOutlined/>}
                loading={uploading}
                disabled={!layerId || layerId === '0'}
              >
                Загрузить
              </Button>
            </Upload>
          </Space>
        </div>
      )}

    </div>
  )
}

export default AdminImportListPage
